/**
 * School Dashboard Page
 * Shows school stats, charts, and recent activity
 */
import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { StatCard } from '../components/shared';
import DemoHealthScoreCard from '../components/demo/DemoHealthScoreCard';
import RichAnalyticsPanel from '../components/analytics/RichAnalyticsPanel';
import { useAuth } from '../App';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// API helper
const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return await res.json();
    } catch (err) {
      console.error('API error:', err);
      return { error: err.message };
    }
  }
};

export default function SchoolDashboard({ token }) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [stats, setStats] = useState(null);
  const [genderData, setGenderData] = useState(null);
  const [classData, setClassData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [feeData, setFeeData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDashboardData(); }, []);

  const loadDashboardData = async () => {
    try {
      const [statsRes, genderRes, classRes, attendanceRes, feeRes, activityRes] = await Promise.all([
        api.get('/api/dashboard/stats', token),
        api.get('/api/dashboard/students/gender', token),
        api.get('/api/dashboard/students/by-class', token),
        api.get('/api/dashboard/attendance/trend?months=6', token),
        api.get('/api/dashboard/fees/monthly?months=6', token),
        api.get('/api/dashboard/recent-activity?limit=8', token)
      ]);
      
      setStats(statsRes);
      setGenderData(genderRes.data || []);
      setClassData(classRes.data || []);
      setAttendanceData(attendanceRes.data || []);
      setFeeData(feeRes.data || []);
      setActivities(activityRes.activities || []);
    } catch (err) {
      console.error('Dashboard load error:', err);
    }
    setLoading(false);
  };

  if (loading) return <div className="p-8 text-center">{L('Loading dashboard...', 'डैशबोर्ड लोड हो रहा है...')}</div>;

  const COLORS = ['#3b82f6', '#ec4899', '#8b5cf6'];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800" data-testid="dashboard-title">{L('School Dashboard', 'स्कूल डैशबोर्ड')}</h2>
        <button onClick={loadDashboardData} className="px-3 py-1 text-sm bg-gray-100 rounded-lg hover:bg-gray-200" data-testid="dashboard-refresh-btn">🔄 {L('Refresh', 'रिफ्रेश')}</button>
      </div>

      {/* Demo Health Score (only visible to demo users) */}
      <DemoHealthScoreCard user={user} token={token} />

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-stat-cards">
        <StatCard title={L('Total Students', 'कुल विद्यार्थी')} value={stats?.total_students || 0} icon="👨‍🎓" color="orange" />
        <StatCard title={L('Total Teachers', 'कुल शिक्षक')} value={stats?.total_teachers || 0} icon="👨‍🏫" color="green" />
        <StatCard title={L("Today's Attendance", 'आज की उपस्थिति')} value={`${stats?.today_attendance?.percentage || 0}%`} icon="✅" color="blue" />
        <StatCard title={L('Monthly Collection', 'मासिक संग्रह')} value={`₹${(stats?.monthly_collection || 0).toLocaleString()}`} icon="💰" color="purple" />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gender Distribution - Pie Chart */}
        <div className="bg-white rounded-xl shadow p-6" data-testid="gender-chart">
          <h3 className="font-semibold text-gray-700 mb-4">{L('Students by Gender', 'लिंग अनुसार विद्यार्थी')}</h3>
          {genderData && genderData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={genderData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {genderData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">No data available</div>
          )}
        </div>

        {/* Attendance Trend - Line Chart */}
        <div className="bg-white rounded-xl shadow p-6" data-testid="attendance-chart">
          <h3 className="font-semibold text-gray-700 mb-4">{L('Attendance Trend (Last 6 Months)', 'उपस्थिति रुझान (पिछले 6 माह)')}</h3>
          {attendanceData && attendanceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={attendanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => [`${value}%`, 'Attendance']} />
                <Line type="monotone" dataKey="attendance" stroke="#f97316" strokeWidth={3} dot={{ fill: '#f97316', strokeWidth: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">No data available</div>
          )}
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fee Collection - Bar Chart */}
        <div className="bg-white rounded-xl shadow p-6" data-testid="fee-chart">
          <h3 className="font-semibold text-gray-700 mb-4">{L('Monthly Fee Collection', 'मासिक फीस संग्रह')}</h3>
          {feeData && feeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={feeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Amount']} />
                <Bar dataKey="amount" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">No data available</div>
          )}
        </div>

        {/* Students by Class - Bar Chart */}
        <div className="bg-white rounded-xl shadow p-6" data-testid="class-chart">
          <h3 className="font-semibold text-gray-700 mb-4">{L('Students per Class', 'कक्षावार विद्यार्थी')}</h3>
          {classData && classData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={classData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
                <Tooltip />
                <Bar dataKey="students" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">No data available</div>
          )}
        </div>
      </div>

      {/* Bottom Row - Recent Activity & Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow p-6" data-testid="recent-activity">
          <h3 className="font-semibold text-gray-700 mb-4">{L('Recent Activity', 'हाल की गतिविधि')}</h3>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {activities.map((activity, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="text-xl">{activity.icon}</span>
                <div className="flex-1">
                  <p className="text-sm text-gray-700">{activity.message}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {activity.time ? new Date(activity.time).toLocaleString('en-IN', { 
                      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                    }) : ''}
                  </p>
                </div>
                {activity.status && (
                  <span className={`text-xs px-2 py-1 rounded ${activity.status === 'open' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {activity.status}
                  </span>
                )}
              </div>
            ))}
            {activities.length === 0 && <div className="text-center py-8 text-gray-400">{L('No recent activity', 'कोई हाल की गतिविधि नहीं')}</div>}
          </div>
        </div>

        {/* Quick Info Panel */}
        <div className="bg-white rounded-xl shadow p-6" data-testid="quick-overview">
          <h3 className="font-semibold text-gray-700 mb-4">{L('Quick Overview', 'त्वरित अवलोकन')}</h3>
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 rounded-lg">
              <p className="text-sm text-orange-600">{L('Open Complaints', 'खुली शिकायतें')}</p>
              <p className="text-2xl font-bold text-orange-700">{stats?.open_complaints || 0}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600">{L('Total Classes', 'कुल कक्षाएं')}</p>
              <p className="text-2xl font-bold text-blue-700">{stats?.total_classes || 0}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-600">{L('Today Present', 'आज उपस्थित')}</p>
              <p className="text-2xl font-bold text-green-700">{stats?.today_attendance?.present || 0} / {stats?.today_attendance?.total || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Rich Analytics (date filter + drill-down) */}
      <RichAnalyticsPanel token={token} mode="school" />
    </div>
  );
}
